import type { AthenaPrincipal } from "../../../runtime/data/principal.ts";
import { AthenaBillingCapabilityError } from "../../errors.ts";
import type { BillingProviderConfigMap } from "../../providers/types.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";
import type { BillingProviderRegistry } from "../local/providers/registry.ts";
import { denyBillingInvocation } from "../rights.ts";
import type { BillingSelfPort } from "../types.ts";
import { createSelfCheckout } from "./checkout.ts";
import {
	isSelfBillingOperation,
	rejectSelfPayloadSelectors,
	type SelfBillingOperation,
} from "./dispatch.ts";
import { enrollSelfSubscription } from "./enroll.ts";
import { getSelfInvoice, listSelfInvoices } from "./invoices.ts";
import { getSelfPayment, listSelfPayments } from "./payments.ts";
import { billingRedirectAllowlistFromAppUrl } from "./redirect-url.ts";
import { resumeSelfCheckout } from "./resume.ts";
import {
	cancelSelfSubscription,
	getSelfSubscription,
} from "./subscriptions.ts";

function asRecord(value: unknown): Record<string, unknown> {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return {};
	}
	return value as Record<string, unknown>;
}

function requireString(record: Record<string, unknown>, key: string): string {
	const value = record[key];
	if (typeof value !== "string" || !value.trim()) {
		throw new AthenaBillingCapabilityError({
			operation: "invoices.get",
			reason: "unsupported_operation",
		});
	}
	return value;
}

export async function executeSelfBillingOperation(input: {
	appUrl?: string | null;
	configuredProviders?: BillingProviderConfigMap;
	operation: SelfBillingOperation;
	payload: unknown;
	principal: AthenaPrincipal;
	registry?: BillingProviderRegistry;
	sql?: BillingSqlExecutor;
	testMode?: boolean;
}): Promise<unknown> {
	rejectSelfPayloadSelectors(input.payload);
	const denied = denyBillingInvocation(input.principal, input.operation, {
		kind: "session",
	});
	if (denied) {
		throw denied;
	}
	if (!input.sql) {
		throw new AthenaBillingCapabilityError({
			operation: input.operation,
			reason: "runtime_unavailable",
		});
	}
	const payload = asRecord(input.payload);
	const allowedRedirectOrigins = billingRedirectAllowlistFromAppUrl(
		input.appUrl,
	);
	switch (input.operation) {
		case "self.invoices.list":
			return listSelfInvoices({
				limit: typeof payload.limit === "number" ? payload.limit : undefined,
				principal: input.principal,
				sql: input.sql,
			});
		case "self.invoices.get":
			return getSelfInvoice({
				invoiceId: requireString(payload, "invoiceId"),
				principal: input.principal,
				sql: input.sql,
			});
		case "self.payments.list":
			return listSelfPayments({
				limit: typeof payload.limit === "number" ? payload.limit : undefined,
				principal: input.principal,
				sql: input.sql,
			});
		case "self.payments.get":
			return getSelfPayment({
				paymentId: requireString(payload, "paymentId"),
				principal: input.principal,
				sql: input.sql,
			});
		case "self.subscription.get":
			return getSelfSubscription({
				principal: input.principal,
				sql: input.sql,
				subscriptionId:
					typeof payload.subscriptionId === "string"
						? payload.subscriptionId
						: undefined,
			});
		case "self.subscription.cancel":
			return cancelSelfSubscription({
				configuredProviders: input.configuredProviders,
				principal: input.principal,
				registry: input.registry,
				sql: input.sql,
				subscriptionId: requireString(payload, "subscriptionId"),
				testMode: input.testMode,
			});
		case "self.subscription.enroll":
			return enrollSelfSubscription({
				allowedRedirectOrigins,
				cancelUrl:
					typeof payload.cancelUrl === "string" ? payload.cancelUrl : undefined,
				configuredProviders: input.configuredProviders,
				idempotencyKey: requireString(payload, "idempotencyKey"),
				principal: input.principal,
				priceId: requireString(payload, "priceId"),
				registry: input.registry,
				sql: input.sql,
				successUrl:
					typeof payload.successUrl === "string" ? payload.successUrl : undefined,
				testMode: input.testMode,
			});
		case "self.checkout.create":
			return createSelfCheckout({
				allowedRedirectOrigins,
				cancelUrl:
					typeof payload.cancelUrl === "string" ? payload.cancelUrl : undefined,
				configuredProviders: input.configuredProviders,
				idempotencyKey: requireString(payload, "idempotencyKey"),
				principal: input.principal,
				priceId: requireString(payload, "priceId"),
				registry: input.registry,
				sql: input.sql,
				successUrl:
					typeof payload.successUrl === "string" ? payload.successUrl : undefined,
				testMode: input.testMode,
			});
		case "self.checkout.resume":
			return resumeSelfCheckout({
				configuredProviders: input.configuredProviders,
				principal: input.principal,
				registry: input.registry,
				returnToken: requireString(payload, "returnToken"),
				sql: input.sql,
				testMode: input.testMode,
			});
		default: {
			const _exhaustive: never = input.operation;
			return _exhaustive;
		}
	}
}

export { createUnavailableBillingSelfPort } from "./unavailable.ts";

export function createSelfBillingPort(input: {
	appUrl?: string | null;
	configuredProviders?: BillingProviderConfigMap;
	principal: AthenaPrincipal;
	registry?: BillingProviderRegistry;
	sql?: BillingSqlExecutor;
	testMode?: boolean;
}): BillingSelfPort {
	const run = async (
		operation: SelfBillingOperation,
		payload: unknown,
	): Promise<unknown> =>
		executeSelfBillingOperation({
			appUrl: input.appUrl,
			configuredProviders: input.configuredProviders,
			operation,
			payload,
			principal: input.principal,
			registry: input.registry,
			sql: input.sql,
			testMode: input.testMode,
		});
	return {
		checkout: {
			create: (payload) =>
				run("self.checkout.create", payload) as ReturnType<
					BillingSelfPort["checkout"]["create"]
				>,
			resume: (payload) =>
				run("self.checkout.resume", payload) as ReturnType<
					BillingSelfPort["checkout"]["resume"]
				>,
		},
		invoices: {
			get: (payload) =>
				run("self.invoices.get", payload) as ReturnType<
					BillingSelfPort["invoices"]["get"]
				>,
			list: (payload) =>
				run("self.invoices.list", payload ?? {}) as ReturnType<
					BillingSelfPort["invoices"]["list"]
				>,
		},
		payments: {
			get: (payload) =>
				run("self.payments.get", payload) as ReturnType<
					BillingSelfPort["payments"]["get"]
				>,
			list: (payload) =>
				run("self.payments.list", payload ?? {}) as ReturnType<
					BillingSelfPort["payments"]["list"]
				>,
		},
		subscription: {
			cancel: (payload) =>
				run("self.subscription.cancel", payload) as ReturnType<
					BillingSelfPort["subscription"]["cancel"]
				>,
			enroll: (payload) =>
				run("self.subscription.enroll", payload) as ReturnType<
					BillingSelfPort["subscription"]["enroll"]
				>,
			get: (payload) =>
				run("self.subscription.get", payload ?? {}) as ReturnType<
					BillingSelfPort["subscription"]["get"]
				>,
		},
	};
}

export { isSelfBillingOperation };
